<template>
    <div class="wrapper wrapper--no-max-width">
        <header class="row__header">
            <h1>{{ $t('profile.postalAddressPage.postalAddress') }}</h1>
        </header>

        <widget-form :title="$t('profile.postalAddressPage.title')"
            class="widget widget--transparent widget--fluid"
        >
            <label :class="labelClass">
                {{ $t('profile.postalAddressPage.street') }}:

                <input v-model.trim="address.street"
                    type="text"
                    :disabled="hasValidatedKycLevel"
                    :placeholder="$t('profile.postalAddressPage.streetHint')"
                    :class="['label__input', { 'label__input--active': address.street }]"
                    @change="onTextChange"
                >
            </label>

            <label :class="labelClass">
                {{ $t('profile.postalAddressPage.zipcode') }}:

                <input v-model.trim="address.zipCode"
                    type="text"
                    :disabled="hasValidatedKycLevel"
                    :placeholder="$t('profile.postalAddressPage.zipcodeHint')"
                    :class="['label__input', { 'label__input--active': address.zipCode }]"
                    @change="onTextChange"
                >
            </label>

            <label :class="labelClass">
                {{ $t('profile.postalAddressPage.city') }}:

                <input v-model.trim="address.city"
                    type="text"
                    :disabled="hasValidatedKycLevel"
                    :placeholder="$t('profile.postalAddressPage.cityHint')"
                    :class="['label__input', { 'label__input--active': address.city }]"
                    @change="onTextChange"
                >
            </label>

            <label :class="labelClass">
                {{ $t('profile.postalAddressPage.region') }}:

                <input v-model.trim="address.region"
                    type="text"
                    :disabled="hasValidatedKycLevel"
                    :placeholder="$t('profile.postalAddressPage.regionHint')"
                    :class="['label__input', { 'label__input--active': address.region }]"
                    @change="onTextChange"
                >
            </label>

            <div :class="['label', 'label--half', 'label--bottom-padding', { 'label--disabled': disableCountryDropdown }]">
                {{ $t('profile.postalAddressPage.country') }}:

                <country-dropdown :country.sync="address.country"
                    :disabled="disableCountryDropdown"
                    :placeholder="$t('kycUserIdentity.countryHint')"
                />

                <span v-if="showDisabledCountryLabel"
                    class="label__hint"
                >
                    {{ $t('kycUserIdentity.countrySubtitle') }}
                </span>
            </div>

            <div class="widget__footer">
                <div v-if="hasValidatedKycLevel">
                    <p class="widget__info-text">
                        {{ $t('profile.webDisabledInfoChange1') }}
                        <a href="https://www.socios.com/help-center/"
                            target="_blank"
                            class="generic-link"
                        >
                            {{ $t('profile.webDisabledInfoChange2') }}
                        </a>
                        {{ $t('profile.webDisabledInfoChange3') }}
                    </p>
                </div>

                <div v-if="!hasValidatedKycLevel"
                    class="widget__buttons"
                >
                    <button id="button-save-level-3"
                        :class="['widget__button widget__button--save', {'widget__button--disabled': !canSave}]"
                        @click="save()"
                    >
                        {{ $t('global.button.save') }}
                    </button>

                    <button id="button-verify-level-3"
                        :class="['widget__button widget__button--verify', {'widget__button--disabled': !canVerify}]"
                        @click="confirm()"
                    >
                        {{ $t('profile.verifyBtn') }}
                    </button>
                </div>
            </div>
        </widget-form>
    </div>
</template>

<script>
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Components
import widgetForm from '@webapp/components/form/widget-form.vue';
import countryDropdown from '@webapp/components/country-dropdown/country-dropdown.vue';

// Local Types
import localStoreTypes from '@webapp/store/types.js';

// Mixins
import postalAddressMixin from '@webapp/helpers/postal-address-mixin.js';

// Helpers
import {
    formatCountry,
} from '@webapp/helpers/country';

export default {
    name: 'postal-address',
    components: {
        widgetForm,
        countryDropdown,
    },
    mixins: [postalAddressMixin],
    data() {
        return {
            isVerify: false,
            isSaveButtonDisabled: false,
            isVerifyButtonDisabled: true,
        };
    },
    computed: {
        ...mapGetters([
            'kycTierLevel',
            'kycTierLevelProcessing',
            'hasUpdatedUserAddress',
            'isKycTierLevelValidated',
        ]),
        canSave() {
            return this.userInteraction && !this.isSaveButtonDisabled;
        },
        canVerify() {
            return this.isFormValid && (this.userInteraction || !this.isVerifyButtonDisabled);
        },
        isChangeCountryDisabled() {
            return this.isKycTierLevelValidated(2);
        },
        currentProcessingKycLevel() {
            return this.kycTierLevelProcessing(3);
        },
        hasValidatedKycLevel() {
            return this.isKycTierLevelValidated(3);
        },
        showDisabledCountryLabel() {
            return this.isChangeCountryDisabled && !this.hasValidatedKycLevel;
        },

        disableCountryDropdown() {
            return this.isChangeCountryDisabled || this.hasValidatedKycLevel;
        },
        userInteraction() {
            const {
                street,
                zipCode,
                city,
                country,
            } = this.address;
            const hasAllData = Boolean(street && zipCode && city && country);

            return hasAllData && this.hasAddressFieldsChanged;
        },
        labelClass() {
            return this.hasValidatedKycLevel ? 'label label--half label--bottom-padding label--disabled' : 'label label--half label--bottom-padding';
        },
    },
    watch: {
        hasUpdatedUserAddress: 'onUpdatedUserAddress',
        userAddress: 'onInitUserAddress',
    },
    created() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });
    },
    methods: {
        ...mapActions({
            setModal: localStoreTypes.SET_MODAL,
            resetModal: localStoreTypes.RESET_MODAL,
            setReverifyKyc: localStoreTypes.SET_REVERIFY_KYC,
        }),

        /**
         * Save the current form
         *
         * @returns {void}
         */
        async save() {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            this.isSaveButtonDisabled = false;
            this.isVerify = false;

            // Check if the User already has a Level 2 request in processing state
            if (this.currentProcessingKycLevel === 3) {
                this.showErrorModal();
            } else {
                await this.updateAddress();
                this.isVerifyButtonDisabled = false;
            }
        },

        /**
         * Confirm current form
         *
         * @returns {void}
         */
        async confirm() {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            this.isVerifyButtonDisabled = true;
            this.isVerify = true;

            if (this.kycTierLevel < 2) {
                this.displayKycModal();
                this.isVerifyButtonDisabled = false;

                this.showAppLoader({
                    isVisible: false,
                });

                return;
            }

            // Check if the User already has a Level 3 request in processing state
            if (this.currentProcessingKycLevel === 3) {
                this.showErrorModal();
                this.isVerifyButtonDisabled = false;
                this.showAppLoader({
                    isVisible: false,
                });

                return;
            }

            await this.updateAddress();
        },

        /**
         * On updated user address
         *
         * @param {boolean} newValue If has updated user address
         * @returns {void}
         */
        onUpdatedUserAddress(newValue) {
            if (newValue && !this.isVerify) {
                this.$notify({
                    type: 'success',
                    text: this.$t('global.updatedSuccessfully'),
                });

                this.isSaveButtonDisabled = false;

                this.showAppLoader({
                    isVisible: false,
                });
            } else if (newValue && this.isVerify) {
                this.goToKycDocuments();
            }
        },

        /**
         * On init user address
         *
         * @param {object} address The address details to be set on init
         * @returns {void}
         */
        onInitUserAddress(address) {
            this.address.street = address.street;
            this.address.city = address.city;
            this.address.region = address.region;
            this.address.zipCode = address.zipCode;
            this.address.country = formatCountry(address.countryCode);

            this.showAppLoader({
                isVisible: false,
            });
        },

        /**
         * On text changed
         *
         * @returns {void}
         */
        onTextChange() {
            this.isSaveButtonDisabled = false;
        },

        /**
         * On route to kyc documents types page
         *
         * @returns {void}
         */
        goToKycDocuments() {
            this.showAppLoader({
                isVisible: false,
            });

            this.setReverifyKyc(true);

            // Redirect to the kyc level 3
            this.$router.push('/kyc/level-3');
        },

        /**
         * On validation error
         *
         * @param {object} error The error received
         * @returns {void}
         */
        onValidationError(error) {
            if (Object.keys(error).length) {
                const {
                    city = '',
                    countryCode = '',
                    region = '',
                    street = '',
                    zipCode = '',
                    httpException = '',
                    user = '',
                } = error;

                this.$notify({
                    type: 'error',
                    text: city || countryCode || region || street || zipCode || httpException || user,
                });
            }

            this.showAppLoader({
                isVisible: false,
            });
        },

        showErrorModal() {
            this.setModal({
                isVisible: true,
                image: require('@webapp/assets/oopps.webp'),
                title: this.$t('kycErrorMessage.title'),
                messages: [
                    {
                        text: this.$t(`kycErrorMessage.errorMsgLevel${this.currentProcessingKycLevel}`),
                    },
                ],
                acceptButton: {
                    text: this.$t('global.button.continue'),
                    action: () => this.$nextTick(() => {
                        this.resetModal();
                    }),
                },
                onDestroy: () => {
                    this.showAppLoader({
                        isVisible: false,
                    });
                },
            });
        },

        displayAppDownloadModal() {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/kyc/kyc-app-download-prompt',
            });
        },

        displayKycModal() {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/kyc/kyc-level-up-modal',
                data: {
                    kycLevel: this.currentProcessingKycLevel,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.widget {
    border: none;

    &__footer {
        display: flex;
        width: 100%;
        justify-content: flex-end;
    }

    &__info-text {
        font-size: 15px;
        line-height: 20px;
        color: var(--black-color);
    }

    &__buttons {
        display: flex;
        width: 100%;
        justify-content: flex-end;

        .widget__button {
            @include min-width(md) {
                width: 21.9rem;
            }

            &:last-child {
                margin-left: 3rem;
            }

            &--disabled {
                opacity: 0.3;
            }

            &--save {
                background: none;
                color: $cyprus-color;
                border: 0.2rem solid $cyprus-color;
            }

            &--verify {
                &.widget__button--disabled {
                    opacity: 1;
                    background-color: $lima-color-opaque;
                    color: $hit-gray-color;
                }
            }
        }
    }
}
</style>